/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import ColStyles from './ColStyles.js';
import { BreakpointsConsumer } from './BreakpointsProvider';
import { devMessage, isDevEnv } from './helpers/devMessage';
import { injectTestId } from '@cvent/nucleus-test-automation';

export default class Col extends React.Component {
  static propTypes = {
    /** The z-index of the Col. Corresponds to css z-index */
    zIndex: PropTypes.number,
    /** Variable-width: numbers <= 1 are interpreted as percentages. ex: 1/2 and .5 evaluate to one-half the width of the Col's parent Row.
     * Fixed-width: numbers > 1 are interpreted as pixels. ex: 100 evaluates to '100px'.
     * auto-width: 'fill' evenly divides the remainder of space not occupied by Col's with fixed or variable widths with other 'fill' width Cols.
     * size-to-children: 'content' Col will take the size of it's content. This is most often used to place buttons and other inline content in a horizontal line. */
    width: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf(['fill', 'content'])
    ]),

    /** Variable-minWidth: numbers <= 1 are interpreted as percentages. ex: 1/2 and .5 evaluate to one-half the width of the Col's parent Row.
     * Fixed-minWidth: numbers > 1 are interpreted as pixels. ex: 100 evaluates to '100px'.*/
    minWidth: PropTypes.number,

    /** Variable-marginStart: numbers <= 1 are interpreted as percentages. ex: 1/2 and .5 evaluate to one-half the width of the Col's parent Row.
     * Fixed-marginStart: numbers > 1 are interpreted as pixels. ex: 100 evaluates to '100px'.*/
    offset: PropTypes.number,

    /** Variable-marginEnd: numbers <= 1 are interpreted as percentages. ex: 1/2 and .5 evaluate to one-half the width of the Col's parent Row.
     * Fixed-marginEnd: numbers > 1 are interpreted as pixels. ex: 100 evaluates to '100px'.*/
    offsetEnd: PropTypes.number,

    /** Units are pixels. If padding is provided as a single number, the Col will have that padding on right and left. If padding is provided as an object, follow the PropType shape given. Omit values that should be inherited from smaller sizes. If `isRtl` prop is false, `start` will evaluate to `paddingLeft`, the opposite is true for RTL */
    padding: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.shape({
        top: PropTypes.number,
        end: PropTypes.number,
        bottom: PropTypes.number,
        start: PropTypes.number
      })
    ]),

    /** `flex` Prop controls the way content inside the Col behaves. The default displays content in a `block` display. The `display` property must be set to 'flex' for 'justifyContent' and 'alignItems' to have an effect. Properties follow standard CSS rules */
    flex: PropTypes.shape({
      display: PropTypes.oneOf(['block', 'flex', 'hidden']),
      justifyContent: PropTypes.oneOf(['flex-start', 'center', 'flex-end']),
      alignItems: PropTypes.oneOf([
        'stretch',
        'flex-start',
        'center',
        'flex-end'
      ])
    }),

    /** Children of Cols can be Rows, or React elements, they may NOT be Cols */
    children: PropTypes.any,

    /** Text direction right-to-left */
    isRtl: PropTypes.bool,

    /** Style to be applied to Cols when viewport width is between S & M breakpoints. Inherits properties from default size */
    s: PropTypes.shape({
      width: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf(['fill', 'content'])
      ]),
      minWidth: PropTypes.number,
      offset: PropTypes.number,
      offsetEnd: PropTypes.number,
      padding: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
          top: PropTypes.number,
          end: PropTypes.number,
          bottom: PropTypes.number,
          start: PropTypes.number
        })
      ]),
      flex: PropTypes.shape({
        display: PropTypes.oneOf(['block', 'flex', 'hidden']),
        justifyContent: PropTypes.oneOf(['flex-start', 'center', 'flex-end']),
        alignItems: PropTypes.oneOf([
          'stretch',
          'flex-start',
          'center',
          'flex-end'
        ])
      })
    }),

    /** Style to be applied to Cols when viewport width is between M & L breakpoints. Inherits properties from default & S sizes */
    m: PropTypes.shape({
      width: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf(['fill', 'content'])
      ]),
      minWidth: PropTypes.number,
      offset: PropTypes.number,
      offsetEnd: PropTypes.number,
      padding: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
          top: PropTypes.number,
          end: PropTypes.number,
          bottom: PropTypes.number,
          start: PropTypes.number
        })
      ]),
      flex: PropTypes.shape({
        display: PropTypes.oneOf(['block', 'flex', 'hidden']),
        justifyContent: PropTypes.oneOf(['flex-start', 'center', 'flex-end']),
        alignItems: PropTypes.oneOf([
          'stretch',
          'flex-start',
          'center',
          'flex-end'
        ])
      })
    }),

    /** Style to be applied to Cols when viewport width is greater than L breakpoint. Inherits properties from default, S, & M sizes */
    l: PropTypes.shape({
      width: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf(['fill', 'content'])
      ]),
      minWidth: PropTypes.number,
      offset: PropTypes.number,
      offsetEnd: PropTypes.number,
      padding: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
          top: PropTypes.number,
          end: PropTypes.number,
          bottom: PropTypes.number,
          start: PropTypes.number
        })
      ]),
      flex: PropTypes.shape({
        display: PropTypes.oneOf(['block', 'flex', 'hidden']),
        justifyContent: PropTypes.oneOf(['flex-start', 'center', 'flex-end']),
        alignItems: PropTypes.oneOf([
          'stretch',
          'flex-start',
          'center',
          'flex-end'
        ])
      })
    })
  };

  static defaultProps = {
    width: 1,
    minWidth: 0,
    offset: 0,
    offsetEnd: 0,
    // padding: 8, set default padding in ColStyles.js
    flex: {},
    s: {},
    m: {},
    l: {}
  };

  render() {
    const props = this.props;
    return (
      <BreakpointsConsumer>
        {({ s, m, l }) => {
          const styles = new ColStyles({
            zIndex: props.zIndex,
            breakpoints: { s: s + 'px', m: m + 'px', l: l + 'px' },
            componentDefaults: {
              width: props.width,
              minWidth: props.minWidth,
              offset: props.offset,
              offsetEnd: props.offsetEnd,
              padding: props.padding,
              flex: props.flex
            },
            s: props.s,
            m: props.m,
            l: props.l,
            isRtl: props.isRtl
          });
          return (
            <div
              {...this.cleanProps()}
              {...injectTestId(props.testID)}
              css={styles}
              data-layout="col"
            >
              {props.children}
            </div>
          );
        }}
      </BreakpointsConsumer>
    );
  }

  cleanProps = () => {
    /* eslint-disable react/prop-types */
    const {
      width,
      minWidth,
      offset,
      offsetEnd,
      padding,
      flex,
      children,
      isRtl,
      s,
      m,
      l,
      style,
      className,
      ...restProps
    } = this.props;
    return restProps;
    /* eslint-enable react/prop-types */
  };

  checkProps = () => {
    isDevEnv(() => {
      const { padding: defaultPadding, s, m, l, id } = this.props;
      function checkForInvalidProperties(padding) {
        if (typeof padding === 'object') {
          Object.keys(padding).forEach(
            key =>
              ![
                'start',
                'paddingStart',
                'end',
                'paddingEnd',
                'top',
                'paddingTop',
                'bottom',
                'paddingBottom'
              ].includes(key) &&
              devMessage(
                `[Col] Padding prop received a property named "${key}" (with a value of ${
                  padding[key]
                }). \nThis property will be ignored. \nValid padding properties are 'start', 'end', 'top', and 'bottom'. ${
                  id ? `\nReported by Col with id="${id}"` : ''
                }`
              )
          );
        }
      }
      return [
        defaultPadding,
        s.padding,
        m.padding,
        l.padding
      ].forEach(property => checkForInvalidProperties(property));
    });
  };
  componentDidMount() {
    this.checkProps();
  }
  componentDidUpdate() {
    this.checkProps();
  }
}
